import React, { useEffect, useState } from "react";
import {
  SafBreadcrumb,
  SafBreadcrumbItem,
} from "@saffron/core-components/react";
import { useLocation, useParams } from "react-router-dom";
import "../../css/breadcrumb.css";

const Breadcrumb = ({ assetId: propAssetId }) => {
  // Get assetId from URL params as fallback
  const { assetId: paramAssetId } = useParams();

  // Use prop value if provided, otherwise use the URL param
  const assetId = propAssetId || paramAssetId;

  const [assetName, setAssetName] = useState("");
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Define breadcrumb display names for routes
  const displayNames = {
    "technology-stacks": "Technology Stacks",
    Addtechstack: "Add Frontend Technology Stack",
    Addbackendtechstack: "Add Backend Technology Stack",
    Adddevopstechstack: "Add DevOps Technology Stack",
    "architecture-details": "Architecture Details",
    summary: "Summary",
  };

  useEffect(() => {
    // First check if we have current asset info in localStorage
    const storedInfo = JSON.parse(localStorage.getItem("assetInfo") || "{}");

    // If the stored assetId matches the current URL assetId, use the stored name
    if (storedInfo && storedInfo.id === assetId && storedInfo.asset_name) {
      setAssetName(storedInfo.asset_name);
    } else if (assetId) {
      // Otherwise fetch the asset name directly
      fetchAssetName(assetId);
    }
  }, [assetId]); // Re-run when assetId changes

  const fetchAssetName = async (id) => {
    if (!id) return;

    try {
      const apiEndpoint = process.env.REACT_APP_MIDDLEWARE_ENDPOINT;
      const operation = "get";
      const endpoint = `${apiEndpoint}&operation=${operation}`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName: process.env.REACT_APP_DB_SOURCE,
          containerName:
            process.env.REACT_APP_CONTAINER_ASSET_INSIGHT_DATASOURCE ||
            process.env.REACT_APP_CONTAINER_FUSION,
          query: `SELECT c.id, c.asset_name FROM c WHERE c.id = '${id}'`,
          partitionKey: id,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const assetData = Array.isArray(data) && data.length > 0 ? data[0] : data;

      if (assetData && assetData.asset_name) {
        setAssetName(assetData.asset_name);

        // Update localStorage with the fresh data
        const updatedInfo = {
          id: assetData.id,
          asset_name: assetData.asset_name,
        };
        localStorage.setItem("assetInfo", JSON.stringify(updatedInfo));
      }
    } catch (err) {
      console.error("Error fetching asset name:", err);
    }
  };

  return (
    <header className="breadcrumb-section">
      <SafBreadcrumb>
        <SafBreadcrumbItem href="/home">Asset Library</SafBreadcrumbItem>
        <SafBreadcrumbItem href={`/${assetId}/summary`}>
          {assetName || "Loading..."}
        </SafBreadcrumbItem>

        {/* Handle main sections - now using assetId consistently */}
        {pathnames.length > 1 &&
          ["summary", "architecture-details", "technology-stacks"].includes(
            pathnames[1]
          ) && (
            <SafBreadcrumbItem href={`/${assetId}/${pathnames[1]}`}>
              {displayNames[pathnames[1]] || pathnames[1]}
            </SafBreadcrumbItem>
          )}

        {/* Handle nested routes - now using assetId consistently */}
        {pathnames.length > 2 && pathnames[1] === "technology-stacks" && (
          <SafBreadcrumbItem>
            {displayNames[pathnames[2]] || pathnames[2]}
          </SafBreadcrumbItem>
        )}

        {/* Handle other routes that don't match the above patterns - now using assetId consistently */}
        {pathnames.length > 1 &&
          !["summary", "architecture-details", "technology-stacks"].includes(
            pathnames[1]
          ) &&
          pathnames.slice(1).map((value, index) => {
            const to = `/${assetId}/${pathnames.slice(1, index + 2).join("/")}`;
            return (
              <SafBreadcrumbItem key={to} href={to}>
                {displayNames[value] || value}
              </SafBreadcrumbItem>
            );
          })}
      </SafBreadcrumb>
    </header>
  );
};

export default Breadcrumb;
